import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { apiGet, apiPost, ApiError } from "@/lib/apiClient";
import { tryCreate, tryDelete, tryGet, tryList, tryUpdate } from "./_shared";

export type Status = Tables<"statuses">;
export type StatusInsert = Omit<TablesInsert<"statuses">, "company_id">;
export type StatusUpdate = TablesUpdate<"statuses">;

export type StatusEntityType = "invoice" | "vendor" | "price_book" | "ticket" | "quote_version" | "price_book_brief";

type StatusWithAudit = Status & {
  created_by_profile?: { full_name: string | null } | null;
  updated_by_profile?: { full_name: string | null } | null;
};

export const statusService = {
  get(id: string) {
    return tryGet<Status>(`/api/statuses/${encodeURIComponent(id)}`);
  },
  getByUniqueName(uniqueName: string, entityType: StatusEntityType, _companyId: string) {
    return tryGet<Status>(
      `/api/statuses?entityType=${encodeURIComponent(entityType)}&uniqueName=${encodeURIComponent(uniqueName)}`,
    );
  },
  listByEntityType(entityType: StatusEntityType, _companyId: string) {
    return tryList<Status>(`/api/statuses?entityType=${encodeURIComponent(entityType)}`);
  },
  listWithAuditInfo(entityType: StatusEntityType, _companyId: string) {
    return tryList<StatusWithAudit>(
      `/api/statuses?entityType=${encodeURIComponent(entityType)}&withAudit=true`,
    );
  },
  getDefault(entityType: StatusEntityType, _companyId: string) {
    return tryGet<Status>(
      `/api/statuses?entityType=${encodeURIComponent(entityType)}&default=true`,
    );
  },
  async getNextSeqNum(entityType: StatusEntityType, _companyId: string): Promise<number> {
    const result = await tryGet<number>(
      `/api/statuses/next-seq-num?entityType=${encodeURIComponent(entityType)}`,
    );
    return result.data ?? 1;
  },
  async countRecordsByStatus(statusId: string, entityType: StatusEntityType): Promise<{ count: number; error: Error | null }> {
    try {
      const result = await apiGet<{ count: number }>(
        `/api/statuses/${encodeURIComponent(statusId)}/usage-count?entityType=${encodeURIComponent(entityType)}`,
      );
      return { count: result.count, error: null };
    } catch (err) {
      return { count: 0, error: err instanceof Error ? err : new Error(String(err)) };
    }
  },
  create(data: StatusInsert, _context: ServiceContext) {
    return tryCreate<Status>("/api/statuses", data);
  },
  update(id: string, data: StatusUpdate, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryUpdate<Status>(`/api/statuses/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/statuses/${encodeURIComponent(id)}`);
  },
  async reassignRecords(
    entityType: StatusEntityType,
    fromStatusId: string,
    toStatusId: string,
    _context?: ServiceContext,
  ): Promise<{ error: Error | null }> {
    try {
      await apiPost("/api/statuses/reassign", {
        entity_type: entityType,
        from_status_id: fromStatusId,
        to_status_id: toStatusId,
      });
      return { error: null };
    } catch (err) {
      if (err instanceof ApiError) return { error: err };
      return { error: err instanceof Error ? err : new Error(String(err)) };
    }
  },
};
